import { ClapperRule, Wordmark } from '../components/Clapper';
import { AppLink } from '../components/Nav';
import { Button } from '../components/ui';

/**
 * Every way to record, from the app itself.
 *
 * The marketing site has the long form at slatevideo.com/download; this is
 * the short one, for a person already signed in who wants Slate on whatever
 * they are holding. It reads the platform and leads with the one that fits,
 * and the environment decides the channel: the development app offers the dev
 * builds, which talk to the development API, so a tester never installs a
 * release build from a dev page and wonders where their recordings went.
 *
 * The desktop files and the extension zip live in the marketing bucket under
 * download/, published by the slate-mac, slate-windows and slate-chrome
 * release workflows. The plain names always mean "latest". The phone apps have
 * no store listing yet, so they are listed with what is true rather than a
 * button that goes nowhere: set STORES below when they ship.
 */
type Platform = 'mac' | 'windows' | 'linux' | 'phone';

function platform(): Platform {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod|Android/.test(ua)) return 'phone';
  if (/Windows/.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'mac';
  return 'linux';
}

const DEV = import.meta.env.VITE_API_URL?.includes('dev-api') ?? false;
const BASE = 'https://slatevideo.com/download';

/** Set the day a listing goes live; the card turns into a link on its own. */
const STORES = {
  chrome: null as string | null,
  ios: null as string | null,
  android: null as string | null,
};

interface Way {
  key: string;
  title: string;
  /** Where the button goes. Null means there is nowhere to send anyone yet. */
  url: string | null;
  /** The downloaded file's name, for the anchor's download attribute. */
  file?: string;
  cta: string;
  requires: string;
  blurb: string;
  steps: string[];
  note?: string;
  /** Shown in place of the button when `url` is null. */
  status?: string;
}

const WAYS: Record<Platform | 'chrome' | 'web', Way> = {
  mac: {
    key: 'mac',
    title: DEV ? 'Slate Dev for Mac' : 'Slate for Mac',
    url: DEV ? `${BASE}/dev/Slate%20Dev.dmg` : `${BASE}/Slate.dmg`,
    file: DEV ? 'Slate Dev.dmg' : 'Slate.dmg',
    cta: 'Download for Mac',
    requires: 'macOS 13 or later, Apple silicon and Intel',
    blurb:
      'Lives in the menu bar, not the Dock. System audio, global hotkeys, and the transcript made on your own machine while you talk.',
    steps: [
      'Open the disk image and drag Slate to Applications.',
      'Launch it. Slate appears in the menu bar, not the Dock.',
      'Sign in. The app opens this site in your browser and comes straight back.',
    ],
    note: 'Notarized by Apple. Updates itself.',
  },
  windows: {
    key: 'windows',
    title: DEV ? 'Slate Dev for Windows' : 'Slate for Windows',
    url: DEV ? `${BASE}/dev/win/Slate-Dev-Setup.exe` : `${BASE}/win/Slate-Setup.exe`,
    file: DEV ? 'Slate-Dev-Setup.exe' : 'Slate-Setup.exe',
    cta: 'Download for Windows',
    requires: 'Windows 10 (version 2004) or later, 64-bit',
    blurb:
      'Lives in the system tray, by the clock. The same recorder, the same hotkeys, the same link on your clipboard the second you stop.',
    steps: [
      'Run the installer. It installs for your user, no administrator needed, and opens Slate when it is done.',
      'Slate lives in the system tray, by the clock. Click it to record.',
      'Sign in. The app opens this site in your browser and comes straight back.',
    ],
    note: 'Signed by MetaCTO through Microsoft Trusted Signing. Updates itself.',
  },
  chrome: {
    key: 'chrome',
    title: 'Slate for Chrome',
    url: STORES.chrome ?? `${BASE}/${DEV ? 'dev/slate-chrome-dev.zip' : 'slate-chrome.zip'}`,
    // Named only while it is a zip: with a store listing there is no file.
    file: STORES.chrome ? undefined : DEV ? 'slate-chrome-dev.zip' : 'slate-chrome.zip',
    cta: STORES.chrome ? 'Add to Chrome' : 'Download the extension',
    requires: 'Chrome 116 or later, and other Chromium browsers',
    blurb:
      'A toolbar button on every page. Record a tab, a window or the whole screen without leaving the browser.',
    steps: STORES.chrome
      ? ['Add it from the Chrome Web Store.', 'Pin Slate to the toolbar.', 'Sign in once, by confirming a code here.']
      : [
          'Download the zip and unzip it.',
          'Open chrome://extensions, turn on Developer mode, choose Load unpacked, and pick the folder.',
          'Pin Slate to the toolbar and sign in once.',
        ],
    note: STORES.chrome
      ? 'Updates from the Web Store.'
      : 'The Web Store listing is not live yet, so this copy does not update itself. Reload it from the extensions page for a new build.',
  },
  phone: {
    key: 'phone',
    title: 'Slate for iPhone and Android',
    url: STORES.ios ?? STORES.android,
    cta: STORES.ios ? 'Get it on the App Store' : 'Get it on Google Play',
    status: 'In testing. Not on the App Store or Google Play yet.',
    requires: 'iOS 16.4 or later, Android 8 or later',
    blurb:
      'Record the whole phone, the camera, or a voice note. Watch what people sent you and reply, wherever you are.',
    steps: [
      'Record the screen through the system broadcast on iPhone, or screen capture on Android.',
      'Pause and resume mid-take. The pause is cut out of the file.',
      'Everything lands in this same library.',
    ],
  },
  web: {
    key: 'web',
    title: 'Record right here',
    url: '/',
    cta: 'Open the recorder',
    requires: 'Nothing to install, any Chromium browser',
    blurb:
      'The Record button in your library is a full recorder: screen, window or tab, camera and microphone, pause and resume.',
    steps: ['Go to your library.', 'Press Record, in the nav bar.', 'Stop, and the link is on your clipboard.'],
  },
  linux: {
    key: 'web',
    title: 'Record right here',
    url: '/',
    cta: 'Open the recorder',
    requires: 'Nothing to install, any Chromium browser',
    blurb:
      'There is no Linux build. The browser recorder does the same job: screen, window or tab, camera and microphone, pause and resume.',
    steps: ['Go to your library.', 'Press Record, in the nav bar.', 'Stop, and the link is on your clipboard.'],
  },
};

/** The lead card first, then everything else in a stable order. */
function order(here: Platform): Way[] {
  const rest: Array<Platform | 'chrome' | 'web'> = ['mac', 'windows', 'chrome', 'phone', 'web'];
  const lead = WAYS[here];
  return [lead, ...rest.filter((key) => WAYS[key].key !== lead.key).map((key) => WAYS[key])];
}

export function DownloadPage() {
  const [lead, ...rest] = order(platform());

  return (
    <div className="flex min-h-full justify-center bg-slate-950 px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="mb-6 flex justify-center">
          <AppLink to="/">
            <Wordmark />
          </AppLink>
        </div>

        <div className="card overflow-hidden">
          <ClapperRule />
          <div className="p-6">
            <span className="text-caption uppercase tracking-[0.08em] text-chalk-faint">Get Slate</span>
            <h1 className="mt-2 text-h3 text-chalk">{lead.title}</h1>
            <p className="mt-2 text-body text-chalk-dim">{lead.blurb}</p>
            <div className="mt-5 flex flex-col gap-2">
              <LeadAction way={lead} />
              <p className="text-caption text-chalk-faint">{lead.requires}</p>
            </div>
            <ol className="mt-5 grid gap-2 pl-5 text-body text-chalk-dim">
              {lead.steps.map((step) => (
                <li key={step} className="list-decimal">
                  {step}
                </li>
              ))}
            </ol>
            {lead.note ? <p className="mt-4 text-caption text-chalk-faint">{lead.note}</p> : null}
          </div>
        </div>

        <p className="mt-6 text-caption uppercase tracking-[0.08em] text-chalk-faint">Everywhere else</p>
        <ul className="mt-2 grid gap-2">
          {rest.map((way) => (
            <li
              key={way.key}
              className="flex items-center justify-between gap-3 rounded-md border border-hairline bg-slate-900/60 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="truncate text-body text-chalk">{way.title}</p>
                <p className="text-caption text-chalk-faint">{way.status ?? way.requires}</p>
              </div>
              <RestAction way={way} />
            </li>
          ))}
        </ul>

        <p className="mt-4 text-center text-caption text-chalk-faint">
          One account covers all of them. Everything you record lands in this library.
        </p>

        {DEV ? (
          <p className="mt-3 text-center text-caption text-caution">
            These are the development builds. They talk to the development API and update from the dev feed.
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** The big button at the top, or the reason there is not one. */
function LeadAction({ way }: { way: Way }) {
  if (!way.url) {
    return (
      <p className="flex items-center gap-2 text-body text-chalk-dim">
        <span className="rounded-xs border border-slate-600 px-1.5 py-0.5 text-caption uppercase tracking-[0.08em] text-chalk-faint">
          Soon
        </span>
        {way.status}
      </p>
    );
  }
  if (way.url.startsWith('/')) {
    return (
      <AppLink to={way.url}>
        <Button variant="primary" size="lg" className="w-full">
          {way.cta}
        </Button>
      </AppLink>
    );
  }
  return (
    <a href={way.url} download={way.file} target={way.file ? undefined : '_blank'} rel="noopener noreferrer">
      <Button variant="primary" size="lg" className="w-full">
        {way.cta}
      </Button>
    </a>
  );
}

/** The small button in the list below. */
function RestAction({ way }: { way: Way }) {
  if (!way.url) return <span className="text-caption text-chalk-faint">Soon</span>;
  if (way.url.startsWith('/')) {
    return (
      <AppLink to={way.url}>
        <Button size="sm">Open</Button>
      </AppLink>
    );
  }
  return (
    <a href={way.url} download={way.file} target={way.file ? undefined : '_blank'} rel="noopener noreferrer">
      <Button size="sm">{way.file ? 'Download' : 'Get'}</Button>
    </a>
  );
}
